import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { api } from '../lib/api';
import Loader from '../components/Loader';

/**
 * Welcome / entry page. Two stacked forms (password OR request-access by email),
 * matching the original Angular `welcome.html` template.
 */
export default function WelcomePage() {
  const navigate = useNavigate();
  const { login, authenticating, invalidCredentials } = useAuth();

  const [password, setPassword] = useState('');
  const [isRequesting, setIsRequesting] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);

  async function submitPass(e: React.FormEvent) {
    e.preventDefault();
    const ok = await login(password);
    if (ok) navigate('/', { replace: true });
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setRequestFailed(false);
    setUserExists(false);
    setRequestSent(false);
    try {
      const res = await api.requestAccess(requestEmail);
      if (res.user_exists) setUserExists(true);
      else if (res.request_sent) setRequestSent(true);
    } catch {
      setRequestFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="entry-page vert-cont">
      <div className="vert-center">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 text-center">
              <p className="eyebrow">Protected portfolio</p>
              <h1>Enter your access password</h1>
              <h2>Most of this work is under NDA. Enter the password I shared with you to view the full portfolio.</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 col-md-offset-3 col-sm-6 col-sm-offset-3 text-center relative">
              {!isRequesting && (
                <form
                  name="passwordForm"
                  id="passwordForm"
                  onSubmit={submitPass}
                  noValidate
                >
                  <label className="field-label" htmlFor="entry-password">
                    Access password
                  </label>
                  <input
                    id="entry-password"
                    type="password"
                    placeholder="Enter password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />{' '}
                  <button className="btn" type="submit" disabled={authenticating}>
                    Unlock portfolio
                  </button>
                  {invalidCredentials && (
                    <div className="invalid-credentials">Password incorrect. Please try again.</div>
                  )}
                </form>
              )}

              <hr />

              {!isRequesting && <div className="no-access-note">
                <p>
                  Don't have access?{' '}
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsRequesting(true);
                    }}
                  >
                    Get it now
                  </a>{' '}
                  by providing your email address.
                </p>
              </div>}

              {isRequesting && (
                <form
                  id="requestForm"
                  name="requestForm"
                  onSubmit={submitEmail}
                >
                  <hr />
                  <label className="field-label" htmlFor="request-email">
                    Your email
                  </label>
                  <input
                    id="request-email"
                    type="email"
                    placeholder="you@example.com"
                    name="email"
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    required
                  />{' '}
                  <button className="btn" type="submit" disabled={submitting}>
                    Request
                  </button>
                  <div className="cancel">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsRequesting(false);
                      }}
                    >
                      cancel
                    </a>
                  </div>
                </form>
              )}

              {userExists && (
                <div>
                  <p>That email already has access, I've re-sent the email with access instructions.</p>
                </div>
              )}
              {requestSent && (
                <div>
                  <p>Thanks! I'll send you an email with access instructions shortly!</p>
                </div>
              )}
              {requestFailed && (
                <div>
                  <p>
                    Oh no! There was an error requesting access, please{' '}
                    <a href="/contact">contact</a> me.
                  </p>
                </div>
              )}

              {submitting && <Loader />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
